import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface StageSelectorProps {
  onSelect: (stageNumber: number) => void;
}

interface HealingStage {
  id: number;
  stage_number: number;
  title_en: string;
  title_zh: string;
  subtitle_en: string | null;
  subtitle_zh: string | null;
  description: string | null;
  target_description: string | null;
  color_theme: string | null;
  is_active: boolean;
}

const StageSelector: React.FC<StageSelectorProps> = ({ onSelect }) => {
  const { t, i18n } = useTranslation(['service-selection', 'certifications']);

  const { data: stages, isLoading, error } = useQuery({
    queryKey: ['healing-stages'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('healing_stages')
        .select('*')
        .eq('is_active', true)
        .order('stage_number', { ascending: true });

      if (error) throw error;
      return data as HealingStage[];
    }
  });

  const getStageColorClasses = (colorTheme: string | null) => {
    switch (colorTheme) {
      case 'red': return 'border-red-200 bg-red-50/50';
      case 'orange': return 'border-orange-200 bg-orange-50/50';
      case 'yellow': return 'border-yellow-200 bg-yellow-50/50';
      case 'green': return 'border-green-200 bg-green-50/50';
      case 'blue': return 'border-blue-200 bg-blue-50/50';
      default: return 'border-amber-200 bg-amber-50/50';
    }
  };

  const getStageColorBg = (colorTheme: string | null) => {
    switch (colorTheme) {
      case 'red': return 'bg-red-500';
      case 'orange': return 'bg-orange-500';
      case 'yellow': return 'bg-yellow-500';
      case 'green': return 'bg-green-500';
      case 'blue': return 'bg-blue-500';
      default: return 'bg-amber-500';
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-32 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive mb-4">Failed to load healing stages</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-center text-muted-foreground mb-6">
        {t('selectStage')}
      </p>
      
      {stages?.map((stage) => (
        <button
          key={stage.stage_number}
          onClick={() => onSelect(stage.stage_number)}
          className={`w-full p-6 rounded-xl border-2 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg text-left ${
            stage.stage_number === 0
              ? 'border-red-200 bg-red-50/50 hover:bg-red-50'
              : getStageColorClasses(stage.color_theme)
          }`}
        >
          <div className="flex items-start gap-4">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 ${
                stage.stage_number === 0 ? 'bg-red-500' : getStageColorBg(stage.color_theme)
              }`}
            >
              {stage.stage_number}
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground mb-1">
                Stage {stage.stage_number}: {i18n.language === 'zh' ? stage.title_zh : stage.title_en}
              </h3>
              {stage.stage_number === 0 && (
                <div className="flex items-center mb-2">
                  <AlertTriangle className="h-4 w-4 text-red-500 mr-1" />
                  <span className="text-sm text-red-600 font-medium">Emergency Stage</span>
                </div>
              )}
              {(stage.subtitle_en || stage.subtitle_zh) && (
                <p className="text-sm text-muted-foreground">
                  {i18n.language === 'zh' ? stage.subtitle_zh : stage.subtitle_en}
                </p>
              )}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
};

export default StageSelector;
